import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGoogleLogin } from "@react-oauth/google";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Mail, 
  Loader2,
  ArrowLeft,
  Eye,
  EyeOff
} from "lucide-react";
import { toast } from "sonner";
import { robustFetch } from "../lib/robustFetch";

const API = process.env.REACT_APP_BACKEND_URL;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function GoogleLoginButton({ onSuccess, loading, label, disabled }) {
  const { GoogleLogin } = require("@react-oauth/google");
  return (
    <div data-testid="google-login-btn" className={disabled ? "opacity-50 pointer-events-none" : ""}>
      <GoogleLogin
        onSuccess={onSuccess}
        onError={() => {}}
        text="continue_with"
        shape="rectangular"
        width="100%"
        locale="fr"
      />
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Login form
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });
  
  // Register form
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: '',
    company_name: ''
  });
  
  // Terms acceptance
  const [termsAccepted, setTermsAccepted] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    try {
      const res = await robustFetch(`${API}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ credential: credentialResponse.credential }),
        retries: 4,
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('ema_user', JSON.stringify(data));
        navigate('/dashboard');
      } else {
        toast.error(data.detail || 'Connexion Google échouée. Merci de réessayer.');
      }
    } catch (err) {
      const msg = err.name === 'AbortError'
        ? "Réseau lent. Merci de réessayer dans un instant."
        : "Erreur de connexion Google. Merci de réessayer.";
      toast.error(msg);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await robustFetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(loginForm),
        retries: 3,
      });
      
      if (response.ok) {
        const user = await response.json();
        navigate('/dashboard', { state: { user }, replace: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Email ou mot de passe incorrect");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    if (registerForm.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(registerForm)
      });
      
      if (response.ok) {
        const user = await response.json();
        toast.success("Compte créé avec succès !");
        navigate('/dashboard', { state: { user }, replace: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'inscription");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas dark:bg-zinc-950 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Back button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
        
        <Card className="card-flat dark:bg-zinc-900 dark:border-zinc-800">
          <CardContent className="p-8">
            {/* Logo */}
            <div className="flex items-center justify-center gap-2 mb-8">
              <img 
                src={require('../assets/ema-logo.png')} 
                alt="Ema - by Proprio Technologies" 
                className="h-32 w-auto"
              />
            </div>
            
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">{t('auth.login')}</TabsTrigger>
                <TabsTrigger value="register">{t('auth.register')}</TabsTrigger>
              </TabsList>
              
              {/* Login Tab */}
              <TabsContent value="login">
                <div className="space-y-4">
                  {/* Google Login */}
                  <GoogleLoginButton 
                    onSuccess={handleGoogleSuccess} 
                    loading={googleLoading}
                    label={t('auth.googleLogin')}
                  />
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t dark:border-zinc-700" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white dark:bg-zinc-900 px-2 text-ink-muted">{t('common.or')}</span>
                    </div>
                  </div>
                  
                  {/* Email Login Form */}
                  <form onSubmit={handleEmailLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">{t('auth.email')}</Label>
                      <Input
                        id="login-email"
                        type="email"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder={t('auth.emailPlaceholder')}
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password">{t('auth.password')}</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showPassword ? "text" : "password"}
                          value={loginForm.password}
                          onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="••••••••"
                          required
                          data-testid="login-password-input"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full btn-primary"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('common.loading')}
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          {t('auth.loginButton')}
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </TabsContent>
              
              {/* Register Tab */}
              <TabsContent value="register">
                <div className="space-y-4">
                  {/* Terms acceptance */}
                  <div className="flex items-start gap-3 p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700">
                    <Checkbox 
                      id="terms-accept-social"
                      checked={termsAccepted}
                      onCheckedChange={setTermsAccepted}
                      className="mt-0.5"
                      data-testid="terms-checkbox-social"
                    />
                    <label htmlFor="terms-accept-social" className="text-sm text-ink-muted cursor-pointer">
                      {t('auth.termsAgreement')}{' '}
                      <Link to="/terms" className="text-brand-primary dark:text-brand-accent hover:underline">
                        {t('auth.termsLink')}
                      </Link>
                      {' '}{t('auth.andThe')}{' '}
                      <Link to="/privacy" className="text-brand-primary dark:text-brand-accent hover:underline">
                        {t('auth.privacyLink')}
                      </Link>
                      {' '}d'Ema. *
                    </label>
                  </div>
                  
                  {/* Google Signup */}
                  <GoogleLoginButton 
                    onSuccess={handleGoogleSuccess} 
                    loading={googleLoading}
                    label={t('auth.googleLogin')}
                    disabled={!termsAccepted}
                  />
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t dark:border-zinc-700" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white dark:bg-zinc-900 px-2 text-ink-muted">{t('common.or')}</span>
                    </div>
                  </div>
                  
                  {/* Email Register Form */}
                  <form onSubmit={handleEmailRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">{t('auth.fullName')} *</Label>
                      <Input
                        id="register-name"
                        value={registerForm.name}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={t('auth.fullNamePlaceholder')}
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-company">{t('auth.companyName')}</Label>
                      <Input
                        id="register-company"
                        value={registerForm.company_name}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, company_name: e.target.value }))}
                        placeholder={t('auth.companyPlaceholder')}
                        data-testid="register-company-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-email">{t('auth.email')} *</Label>
                      <Input
                        id="register-email"
                        type="email"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder={t('auth.emailPlaceholder')}
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-password">{t('auth.password')} *</Label>
                      <div className="relative">
                        <Input
                          id="register-password"
                          type={showPassword ? "text" : "password"}
                          value={registerForm.password}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="Min. 6 caractères"
                          required
                          data-testid="register-password-input"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full btn-primary"
                      disabled={loading || !termsAccepted}
                      data-testid="register-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('common.loading')}
                        </>
                      ) : (
                        t('auth.registerButton')
                      )}
                    </Button>
                  </form>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        {/* Free trial info */}
        <p className="text-center text-sm text-ink-muted mt-4">
          {t('auth.freeTrialCredits')}
        </p>
      </div>
    </div>
  );
}
